import { CaretDown, CaretUp, List, X } from "@phosphor-icons/react"
import { IconButton } from "@tokenterminal/core/ui/button/IconButton"
import { LinkButton } from "@tokenterminal/core/ui/button/LinkButton"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import Link from "next/link"
import {
  type CSSProperties,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { Popover } from "react-aria-components"
import { twMerge } from "tailwind-merge"
import { navLinks } from "@/constants/nav"
import Container from "./Container"
import { Logo } from "./Logo"
import { SublinkItem } from "./SublinkItem"
import { useBreakpoint } from "./useBreakpoint"

const defaultPopoverTransition = "transform 900ms cubic-bezier(0.03, 1.14, 0, 1), opacity 50ms"

const focusVisibleCss =
  "outline-none rounded-md focus-visible:outline-sky-500 focus-visible:outline-[2px] focus-visible:outline-offset-2"

const popoverClass = twMerge(
  "tt-popover bg-bg-popover/80 backdrop-blur-[20px] p-0 rounded-xl overflow-visible shadow-[0_2px_20px_rgba(0,0,0,0.025),0_15px_20px_rgba(0,0,0,0.05)] dark:shadow-[0_2px_20px_rgba(0,0,0,0.1),0_15px_40px_rgba(0,0,0,0.5)]",
)

const navLinkCss = twMerge(
  "text-[14px] font-medium text-fg-secondary no-underline hover:text-fg-default/90 [&[data-is-open=true]]:text-fg-default",
  focusVisibleCss,
)

export const popoverDropdownLinkCss =
  "group flex items-center gap-2 px-2.5 py-2 no-underline w-full lg:w-[190px] text-[14px] font-medium text-fg-default rounded-md bg-transparent border-none hover:bg-neutral-400/[0.07]"

const DesktopHeader = () => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [openPopoverHref, setOpenPopoverHref] = useState<string | null>(null)
  const popoverAnchorRef = useRef<HTMLDivElement>(null)

  const popoverRef = useRef<HTMLDivElement>(null)

  const popoverCloseTimeout = useRef<NodeJS.Timeout | null>(null)

  const openPopover = useCallback((href: string) => {
    if (popoverCloseTimeout.current) {
      clearTimeout(popoverCloseTimeout.current)
    }

    setIsPopoverOpen(true)
    setOpenPopoverHref(href)
  }, [])

  const closePopover = useCallback(() => {
    popoverCloseTimeout.current = setTimeout(() => {
      setIsPopoverOpen(false)
    }, 200)
  }, [])

  const openPopoverChildren = useMemo(() => {
    const openPopoverLink = navLinks.find((link) => link.href === openPopoverHref)
    if (!openPopoverHref || !openPopoverLink) {
      return null
    }

    const sublinksContent = openPopoverLink.renderDesktopSublinks
      ? openPopoverLink.renderDesktopSublinks()
      : openPopoverLink.sublinks?.map((sublink) => (
          <SublinkItem
            key={sublink.label}
            href={sublink.href}
            label={sublink.label}
            showArrow={true}
          />
        ))

    if (!sublinksContent) {
      return null
    }

    return (
      <div
        key={openPopoverHref}
        className="flex flex-col gap-0.5 max-w-full animate-fade-in p-1.5"
        onMouseEnter={() => {
          openPopover(openPopoverHref)
        }}
        onMouseLeave={closePopover}
        style={{
          animationDuration: "100ms",
          animationDelay: "0ms",
        }}
      >
        {sublinksContent}
      </div>
    )
  }, [openPopoverHref, openPopover, closePopover])

  useEffect(() => {
    const leftTransition = "left 400ms cubic-bezier(0.03, 1.14, 0, 1)"
    if (!popoverRef.current) {
      return
    }

    let timeoutId: NodeJS.Timeout | null = null
    // When opening the popover, after the transition, we'll add a transition to the left property
    // After the popover is closed, we'll remove the transition from the left property
    if (isPopoverOpen) {
      const transitionWithLeft = `${defaultPopoverTransition}, ${leftTransition}`
      timeoutId = setTimeout(() => {
        popoverRef.current?.style.setProperty("transition", transitionWithLeft)
      }, 200)
    } else {
      timeoutId = setTimeout(() => {
        popoverRef.current?.style.setProperty("transition", defaultPopoverTransition)
      }, 200)
    }

    return () => {
      clearTimeout(timeoutId)
    }
  }, [isPopoverOpen])

  return (
    <div
      className="hidden lg:flex gap-2 items-center justify-between"
      style={{
        padding: "16px 20px",
        margin: "0 -20px",
      }}
    >
      <div className="flex-1 flex justify-start">
        <Link href="/" className={focusVisibleCss}>
          <Logo />
        </Link>
      </div>
      <div ref={popoverAnchorRef} className="flex gap-8 items-center p-2">
        {navLinks.map((link) => {
          const hasSublinks = link.sublinks && link.sublinks.length > 0
          if (hasSublinks) {
            return (
              <button
                key={link.href}
                className={navLinkCss}
                onClick={() => {
                  if (hasSublinks) {
                    openPopover(link.href)
                  }
                }}
                onMouseEnter={() => {
                  if (hasSublinks) {
                    openPopover(link.href)
                  }
                }}
                onMouseLeave={() => {
                  if (hasSublinks) {
                    closePopover()
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "ArrowDown") {
                    e.preventDefault()
                    if (hasSublinks) {
                      openPopover(link.href)
                    }
                  }
                }}
                data-is-open={isPopoverOpen && openPopoverHref === link.href}
              >
                {link.label}
              </button>
            )
          }

          return (
            <Link key={link.href} href={link.href} className={navLinkCss}>
              {link.label}
            </Link>
          )
        })}
      </div>
      <div className="flex gap-2 items-center justify-end" style={{ flex: 1 }}>
        <LinkButton color="success" id="explore-button" href="/explorer">
          Go to Explorer
        </LinkButton>
      </div>
      <Popover
        ref={popoverRef}
        className={popoverClass}
        isOpen={isPopoverOpen}
        placement="bottom"
        data-placement="bottom"
        onOpenChange={closePopover}
        triggerRef={popoverAnchorRef}
        isNonModal
        style={
          {
            "--origin": "translateY(-8px)",
            transition: defaultPopoverTransition,
          } as CSSProperties
        }
      >
        {openPopoverChildren}
      </Popover>
    </div>
  )
}

const MobileHeader = () => {
  // 1024 is the lg breakpoint in tailwind
  const isMobile = useBreakpoint({ minWidth: 0, maxWidth: 1024 })

  const [expandedLinkHrefs, setExpandedLinkHrefs] = useState<string[]>([])
  const [isMobilePopoverOpen, setIsMobilePopoverOpen] = useState(false)
  const mobileTriggerRef = useRef<HTMLButtonElement>(null)

  // If isMobile is false and the popover is open, close it
  useEffect(() => {
    setIsMobilePopoverOpen((prev) => {
      if (!isMobile && prev) {
        return false
      }
      return prev
    })
  }, [isMobile])

  return (
    <div className="px-5 py-3 hidden max-lg:flex mx-[-20px]">
      <div className="flex-1 flex items-center">
        <Link href="/" className={focusVisibleCss}>
          <Logo />
        </Link>
      </div>
      <div className="flex gap-1 items-center justify-end" style={{ flex: 1 }}>
        <IconButton
          ref={mobileTriggerRef}
          variant="ghost"
          className="-mr-2 h-auto p-3"
          onPress={() => setIsMobilePopoverOpen(!isMobilePopoverOpen)}
          icon={isMobilePopoverOpen ? X : List}
          label={isMobilePopoverOpen ? "Close menu" : "Open menu"}
        />
        <Popover
          className={popoverClass}
          isOpen={isMobilePopoverOpen}
          placement="bottom"
          onOpenChange={setIsMobilePopoverOpen}
          triggerRef={mobileTriggerRef}
          style={
            {
              "--starting-scale": "scale(1)",
              width: "calc(100vw - 20px)",
            } as CSSProperties
          }
        >
          <div className="flex flex-col gap-0.5 max-w-full p-1.5">
            {navLinks.map((link) => {
              const isExpanded = expandedLinkHrefs.includes(link.href)

              const toggleExpanded = () => {
                setExpandedLinkHrefs((prev) =>
                  prev.includes(link.href)
                    ? prev.filter((href) => href !== link.href)
                    : [...prev, link.href],
                )
              }

              if (link.sublinks) {
                return (
                  <Fragment key={link.href}>
                    <button
                      className={popoverDropdownLinkCss}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                      onClick={toggleExpanded}
                    >
                      {link.label}
                      <Icon as={isExpanded ? CaretUp : CaretDown} />
                    </button>
                    <div
                      className="flex flex-col gap-2 pl-6 max-w-full"
                      style={{
                        display: isExpanded ? "block" : "none",
                      }}
                    >
                      {link.sublinks.map((sublink) => (
                        <SublinkItem
                          key={sublink.label}
                          href={sublink.href}
                          label={sublink.label}
                          showArrow={false}
                        />
                      ))}
                    </div>
                  </Fragment>
                )
              }

              return (
                <Link key={link.label} href={link.href} className={popoverDropdownLinkCss}>
                  {link.label}
                </Link>
              )
            })}
            <LinkButton color="success" variant="solid" id="explore-button" href="/explorer">
              Go to Explorer
            </LinkButton>
          </div>
        </Popover>
      </div>
    </div>
  )
}

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 0) {
        setIsScrolled(true)
      } else {
        setIsScrolled(false)
      }
    }

    handleScroll()

    window.addEventListener("scroll", handleScroll)
    return () => {
      window.removeEventListener("scroll", handleScroll)
    }
  }, [])

  return (
    <div
      className={twMerge(
        "sticky top-0 w-full z-[100] bg-transparent backdrop-blur-none border-b border-solid border-transparent transition-[background,border-color,backdrop-filter] duration-400",
        "data-[is-scrolled=true]:bg-bg-surface/75 data-[is-scrolled=true]:backdrop-blur-[20px] data-[is-scrolled=true]:border-b-border-default",
      )}
      data-is-scrolled={isScrolled}
    >
      <Container>
        <DesktopHeader />
        <MobileHeader />
      </Container>
    </div>
  )
}

export default Header
